import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Box,
  Typography,
  Alert,
  TextField,
  CircularProgress,
  Autocomplete,
  Checkbox,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DescriptionIcon from '@mui/icons-material/Description';
import { SelectedManifest } from '../utils/types';
import { ReceiptEventPackageProps, ReceiptEventSiteProps } from '../../../datastore/ssccs';
import { VirtualizedListboxLarge } from '../components/VirtualizedListbox';
import { AsnStatus } from '../../../common/status';

interface AddASNToSSCCDialogProps {
  open: boolean;
  selectedManifest: SelectedManifest | null;
  ssccId: string;
  availableASNs: ReceiptEventPackageProps[];
  selectedASNId: string;
  selectedReceiptEvent: string;
  receiptedEvents: ReceiptEventSiteProps[];
  withReceiveManifest: boolean;
  isProcessing: boolean;
  onClose: () => void;
  onASNIdChange: (asnId: string) => void;
  onReceiptEventChange: (event: string) => void;
  onReceiveManifestChange: (value: boolean) => void;
  onConfirm: (reason: string) => void;
}

export default function AddASNToSSCCDialog({
  open,
  selectedManifest,
  ssccId,
  availableASNs,
  selectedASNId,
  selectedReceiptEvent,
  receiptedEvents,
  withReceiveManifest,
  isProcessing,
  onClose,
  onASNIdChange,
  onReceiptEventChange,
  onReceiveManifestChange,
  onConfirm
}: AddASNToSSCCDialogProps) {
  const [reasonDraft, setReasonDraft] = useState('');

  useEffect(() => {
    if (open) {
      setReasonDraft('');
    }
  }, [open]);

  const cancelDisabledHint = isProcessing ? 'Please wait until the current action finishes.' : '';
  const confirmDisabledHint = isProcessing
    ? 'Add ASN is in progress.'
    : !selectedASNId
      ? 'Select an ASN to continue.'
      : !selectedReceiptEvent
        ? 'Select a receipted event to continue.'
        : !reasonDraft.trim()
          ? 'Reason for adding is required.'
          : '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
        }
      }}>
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: '#fff',
          px: 3,
          py: 3
        }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              display: 'flex',
              backdropFilter: 'blur(10px)'
            }}>
            <AddIcon sx={{ fontSize: 24, color: '#fff' }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={600} sx={{ color: '#fff' }}>
              Add ASN to SSCC
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.85)', mt: 0.5 }}></Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 3, '& > :first-of-type': { mt: 1 } }}>
        <Stack spacing={3}>
          <Alert
            severity="info"
            icon={<InfoOutlinedIcon />}
            sx={{
              borderRadius: 2,
              border: '1px solid rgba(2, 136, 209, 0.2)',
              backgroundColor: 'rgba(2, 136, 209, 0.04)',
              fontSize: '0.875rem',
              '& .MuiAlert-icon': {
                color: '#0288d1'
              }
            }}>
            {selectedManifest ? (
              <>
                Adding this ASN will update its status to{' '}
                <Box component="span" sx={{ fontWeight: 700, color: '#0277bd' }}>
                  '
                  {availableASNs.find((asn) => asn.asn === selectedASNId)?.status === AsnStatus.available
                    ? AsnStatus.available
                    : selectedManifest.asnStatus}
                  '
                </Box>
                {'. '}
                {selectedManifest?.manifest?.status === 'Completed' && (
                  <>You can choose whether or not to receive the manifest.</>
                )}
              </>
            ) : (
              <>Adding this ASN to the unassigned SSCC. Note: The SSCC is not currently in any manifest.</>
            )}
          </Alert>

          <Box
            sx={{
              p: 2.5,
              borderRadius: 2,
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
              border: '2px solid rgba(25, 118, 210, 0.15)'
            }}>
            <Typography
              variant="caption"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: 0.8,
                fontSize: '0.7rem',
                fontWeight: 700,
                color: '#1976d2'
              }}>
              SSCC ID
            </Typography>
            <Typography variant="h6" fontWeight={600} sx={{ mt: 1, color: '#1565c0' }}>
              {ssccId || 'N/A'}
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                mb: 1.5,
                fontSize: '0.875rem'
              }}>
              Select ASN{' '}
              <Box component="span" sx={{ color: 'error.main' }}>
                *
              </Box>
            </Typography>
            <Typography
              variant="caption"
              sx={{ display: 'block', color: 'text.secondary', mb: 1, fontSize: '0.75rem' }}>
              Eligible ASNs are those not already assigned to this SSCC and not in Available status.
            </Typography>
            <Autocomplete
              options={availableASNs}
              getOptionLabel={(option) => `${option.asn || 'N/A'} - ${option.po || 'No PO'}`}
              value={availableASNs.find((a) => a.asn === selectedASNId) || null}
              onChange={(_, newValue) => onASNIdChange(newValue?.asn || '')}
              ListboxComponent={VirtualizedListboxLarge as React.ComponentType<React.HTMLAttributes<HTMLElement>>}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search for ASN..."
                  helperText="Select an ASN to add to this SSCC"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      fontSize: '0.875rem'
                    }
                  }}
                />
              )}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                  <li key={key} {...optionProps}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%', py: 1 }}>
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: 1.5,
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          display: 'flex'
                        }}>
                        <DescriptionIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {option.asn || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.po ? `PO: ${option.po}` : 'No PO'} •{' '}
                          {option.supplierName || option.companyName || 'Unknown supplier'}
                          {option.quantity ? ` • Qty: ${option.quantity}` : ''}
                        </Typography>
                        {option.status && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                            Status: {option.status}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </li>
                );
              }}
            />
          </Box>

          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                mb: 1.5,
                fontSize: '0.875rem'
              }}>
              Select RECEIPTED EVENT to be added to{' '}
              <Box component="span" sx={{ color: 'error.main' }}>
                *
              </Box>
            </Typography>
            <Typography
              variant="caption"
              sx={{ display: 'block', color: 'text.secondary', mb: 1, fontSize: '0.75rem' }}>
              Eligible receipted events are events that already exist on this SSCC.
            </Typography>
            <Autocomplete
              options={receiptedEvents.map(({ name, site }, index) => `${index + 1}. ${name} - ${site}`)}
              value={selectedReceiptEvent || null}
              onChange={(_, newValue) => onReceiptEventChange(newValue || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search for RECEIPTED EVENT..."
                  helperText="Select a RECEIPTED EVENT to let ASN to be added to"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      fontSize: '0.875rem'
                    }
                  }}
                />
              )}
            />
          </Box>

          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                mb: 1.5,
                fontSize: '0.875rem'
              }}>
              Reason for Adding{' '}
              <Box component="span" sx={{ color: 'error.main' }}>
                *
              </Box>
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              required
              value={reasonDraft}
              onChange={(e) => setReasonDraft(e.target.value)}
              placeholder="Explain why this SSCC is being added to the manifest (e.g., received without manifest, system error, etc.)..."
              helperText="This reason will be logged in the audit trail"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '0.875rem'
                }
              }}
            />
          </Box>

          {selectedManifest?.manifest?.status === 'Completed' ? (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                border: '1px solid rgba(0, 0, 0, 0.08)'
              }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Checkbox
                  size="small"
                  checked={withReceiveManifest}
                  onChange={(e) => onReceiveManifestChange(e.target.checked)}
                />
                <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.875rem' }}>
                  Receive Manifest
                </Typography>
              </Stack>
            </Box>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2.5,
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
          borderTop: '1px solid rgba(0, 0, 0, 0.08)',
          gap: 1.5
        }}>
        <Tooltip title={cancelDisabledHint} disableHoverListener={!cancelDisabledHint} arrow>
          <span>
            <Button
              onClick={onClose}
              disabled={isProcessing}
              startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                color: 'text.secondary',
                fontSize: '0.875rem',
                px: 2.5,
                py: 1
              }}>
              Cancel
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={confirmDisabledHint} disableHoverListener={!confirmDisabledHint} arrow>
          <span>
            <Button
              variant="contained"
              onClick={() => onConfirm(reasonDraft)}
              disabled={!selectedASNId || !selectedReceiptEvent || !reasonDraft.trim() || isProcessing}
              startIcon={
                isProcessing ? <CircularProgress size={16} color="inherit" /> : <AddIcon sx={{ fontSize: 18 }} />
              }
              sx={{
                px: 3,
                py: 1,
                fontWeight: 600,
                fontSize: '0.875rem',
                textTransform: 'none',
                backgroundColor: '#1976d2',
                '&:hover': { backgroundColor: '#1565c0' },
                boxShadow: 'none',
                borderRadius: 1.5
              }}>
              {isProcessing ? 'Adding...' : 'Add ASN'}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}
